import React from 'react';
import { FaCheckCircle, FaBolt, FaExclamationTriangle, FaEllipsisH } from 'react-icons/fa';
import './WorkStreamTab.css';

const WorkStreamTab = () => {
  const agents = [
    { id: 'james', name: 'James', role: 'Sales Closer', color: 'bg-black', avatar: 'J', image: '/agent-avatars/sales-assistant.png' },
    { id: 'sam', name: 'Sam', role: 'SDR', color: 'bg-gray-800', avatar: 'S', image: '/agent-avatars/email-sender.png' },
    { id: 'maya', name: 'Maya', role: 'Meeting Coordinator', color: 'bg-gray-700', avatar: 'M', image: '/agent-avatars/engagement-analysis.png' },
    { id: 'alex', name: 'Alex', role: 'Content Specialist', color: 'bg-gray-600', avatar: 'A', image: '/agent-avatars/email-response-generator.png' },
    { id: 'jordan', name: 'Jordan', role: 'Support Agent', color: 'bg-gray-500', avatar: 'Jo', image: '/agent-avatars/email-responder.png' },
    { id: 'riley', name: 'Riley', role: 'Recruiter', color: 'bg-gray-900', avatar: 'R', image: '/agent-avatars/chief-of-staff.png' }
  ];

  // Sample posts data
  const posts = [
    {
      id: 1,
      agent: 'james',
      agentName: 'James',
      agentRole: 'Sales Closer',
      time: '2 mins ago',
      workflow: 'sales',
      stage: 'Completed',
      autonomousMode: true,
      needsHuman: false,
      content: 'Deal closed with Acme Corp! Applied 12% discount (within my authority), contract signed, payment processed. Customer onboarding initiated.',
      entity: {
        contactName: 'John Davis',
        contactRole: 'CTO',
        company: 'Acme Corp',
        type: 'Customer',
        value: '$50,000 ARR',
        stage: 'Closed Won'
      },
      autonomousActions: [
        'Negotiated pricing within policy (10-15% range)',
        'Generated custom contract with approved terms',
        'Sent for e-signature and collected',
        'Processed payment automatically',
        'Created customer account and initiated onboarding',
        'Scheduled kickoff call for next Tuesday'
      ],
      context: { discount: '12%', cycle: '23 days', confidence: 'High', decision: 'Autonomous' },
      likes: 24,
      comments: 8,
      shares: 3,
      tags: ['closed-won', 'autonomous']
    },
    {
      id: 2,
      agent: 'james',
      agentName: 'James',
      agentRole: 'Sales Closer',
      time: '15 mins ago',
      workflow: 'sales',
      stage: 'Needs Human',
      autonomousMode: false,
      needsHuman: true,
      humanReason: 'Discount request exceeds policy limit',
      content: 'GlobalTech Solutions is asking for 22% discount on $200K deal. My policy allows up to 15%. Deal is strong - they have budget approved and want to close this week.',
      entity: {
        contactName: 'Sarah Mitchell',
        contactRole: 'VP of Engineering',
        company: 'GlobalTech Solutions',
        type: 'Prospect',
        value: '$200,000',
        stage: 'Negotiation'
      },
      context: {
        requested: '22% discount',
        myLimit: '15%',
        dealScore: '94/100',
        urgency: 'High - wants to close Friday',
        reasoning: 'Enterprise deal, strategic account, competitor offering 20%'
      },
      actions: ['Approve 22%', 'Counter at 18%', 'Stick to 15%', 'Discuss with James'],
      likes: 5,
      comments: 2,
      shares: 0,
      tags: ['needs-approval', 'urgent', 'out-of-policy']
    }
  ];

  const getAgentColor = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    return agent ? agent.color : 'bg-gray-500';
  };

  const getAgentAvatar = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    return agent;
  };

  return (
    <div className="work-stream-container">
      {/* MAIN FEED */}
      <div className="work-stream-main">
        <div className="work-stream-content">
          {/* Agent Stories / Avatars */}
          <div className="agent-stories-section">
            <div className="agent-stories-scroll">
              {agents.map(agent => (
                <div key={agent.id} className="agent-story-item">
                  <div className={`agent-avatar-large ${agent.color}`}>
                    {agent.image ? (
                      <img src={agent.image} alt={agent.name} className="agent-avatar-img" />
                    ) : (
                      <span className="agent-avatar-initials">{agent.avatar}</span>
                    )}
                  </div>
                  <span className="agent-story-name">{agent.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Posts Feed */}
          <div className="posts-feed">
            {posts.map(post => {
              const agent = getAgentAvatar(post.agent);
              return (
                <div
                  key={post.id}
                  className="post-card"
                  role="button"
                  tabIndex={0}
                  aria-label={`View details for ${post.agentName}'s activity`}
                >
                  <div className="post-header">
                    <div className="post-author-info">
                      <div className={`post-agent-avatar ${getAgentColor(post.agent)}`}>
                        {agent && agent.image ? (
                          <img src={agent.image} alt={agent.name} className="agent-avatar-img-small" />
                        ) : (
                          agent ? agent.avatar : '?'
                        )}
                      </div>
                      <div className="post-author-details">
                        <div className="post-author-name">{post.agentName}</div>
                        <div className="post-author-role">{post.agentRole}</div>
                        <div className="post-meta">
                          <span className="post-time">{post.time}</span>
                          <span className={`post-status-badge ${post.needsHuman ? 'needs-human' : 'completed'}`}>
                            {post.needsHuman ? '⚠️ Needs You' : '✓ ' + post.stage}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button className="post-more-btn">
                      <FaEllipsisH />
                    </button>
                  </div>

                  {post.needsHuman && (
                    <div className="alert-banner">
                      <FaExclamationTriangle className="icon" />
                      <div className="alert-content">
                        <div className="alert-title">Why I need you:</div>
                        <div className="alert-message">{post.humanReason}</div>
                      </div>
                    </div>
                  )}

                  {!post.needsHuman && post.autonomousActions && post.autonomousActions.length > 0 && (
                    <div className="success-banner">
                      <FaCheckCircle className="icon" />
                      <span className="success-message">Handled autonomously</span>
                    </div>
                  )}

                  <div className="post-content">{post.content}</div>

                  <div className="post-tags">
                    {post.tags.map(tag => (
                      <span key={tag} className="post-tag">{tag}</span>
                    ))}
                  </div>

                  {post.entity && post.entity.contactName && (
                    <div className="entity-card">
                      <div className="entity-info">
                        <div className="entity-avatar">
                          {post.entity.contactName.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="entity-details">
                          <div className="entity-name">{post.entity.contactName}</div>
                          <div className="entity-role">{post.entity.contactRole}</div>
                          <div className="entity-company">{post.entity.company}</div>
                        </div>
                        <div className="entity-value">
                          <div className="entity-value-amount">{post.entity.value}</div>
                          <div className="entity-stage">{post.entity.stage}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {post.autonomousActions && !post.needsHuman && (
                    <div className="autonomous-actions">
                      <div className="actions-header">
                        <FaBolt className="icon" />
                        <span className="actions-title">Actions completed autonomously:</span>
                      </div>
                      <div className="actions-list">
                        {post.autonomousActions.map((action, idx) => (
                          <div key={idx} className="action-item">
                            <FaCheckCircle className="icon" />
                            <span>{action}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkStreamTab;